import ArrowUp16 from '@carbon/icons-react/es/arrow--up/16';
import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import View16 from '@carbon/icons-react/es/view/16';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Menu } from 'components/atoms/dropdown/menu';
import { ThisWeek16 } from 'components/atoms/icons/this-week';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import {
  ProjectTemplateProjectPhase,
  useProjectTemplateProjectPhase,
} from 'data/templates/projects';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslation } from 'hooks/use-translation';
import { ShiftDayModal } from '../shift-day-modal';
import { useTemplateProjectPhaseCommonActions } from './actions';

const renderMenu = ({
  projectPhase,
  onTogglePrivate,
  onDeleteProjectPhase,
  addPhaseAbove,
  setShiftDayModalVisible,
}: {
  projectPhase: ProjectTemplateProjectPhase;
  onTogglePrivate: () => void;
  onDeleteProjectPhase: () => void;
  addPhaseAbove: () => void;
  setShiftDayModalVisible: (boolean) => void;
}) => (
  <Menu>
    <Menu.Header>
      <FormattedMessage id="more_actions" />
    </Menu.Header>
    <Menu.Item
      data-cy="project-template-phase-more-actions-add-phase-above"
      icon={<ArrowUp16 />}
      key="add_phase_above"
      onClick={addPhaseAbove}
    >
      <FormattedMessage id="templates.more_actions.add_phase_above" />
    </Menu.Item>
    <Menu.Item
      data-cy="project-template-phase-more-actions-shift-days"
      icon={<ThisWeek16 />}
      key="shift_days"
      onClick={() => setShiftDayModalVisible(true)}
    >
      <FormattedMessage id="templates.more_actions.shift_days" />
    </Menu.Item>
    <Menu.Item
      data-cy={
        projectPhase.private
          ? 'project-template-phase-more-actions-make-shared'
          : 'project-template-phase-more-actions-make-private'
      }
      icon={projectPhase.private ? <View16 /> : <ViewOff16 />}
      onClick={onTogglePrivate}
      key="change_privacy"
    >
      <FormattedMessage
        id={
          projectPhase.private
            ? 'templates.more_actions.make_shared'
            : 'templates.more_actions.make_private'
        }
      />
    </Menu.Item>
    <Menu.Divider />
    <Menu.ItemDelete
      key="delete"
      onClick={onDeleteProjectPhase}
      data-cy="project-template-phase-more-actions-delete"
    />
  </Menu>
);

export const ProjectPhaseMoreActions: FC<{
  projectPhaseId: number;
  performAutoFocus?: () => void;
}> = ({ projectPhaseId, performAutoFocus }) => {
  const projectPhase: ProjectTemplateProjectPhase =
    useProjectTemplateProjectPhase(projectPhaseId);

  const {
    onDeleteProjectPhase,
    onTogglePrivate,
    onShiftStartDay,
    addPhaseAbove,
  } = useTemplateProjectPhaseCommonActions(projectPhase);

  const modalTitle = useTranslation('templates.shift_modal.title', {
    phaseName: projectPhase.projectPhaseName,
  });
  const deleteConfirmTitle = useTranslation(
    'templates.more_actions.delete.title',
  );
  const deleteConfirmText = useTranslation(
    'templates.more_actions.delete.phase.text',
  );

  const DeleteConfirmation = useDeleteConfirmModal({
    title: deleteConfirmTitle,
    content: deleteConfirmText,
    onOk: () => onDeleteProjectPhase(),
  });

  const [shiftDayModalVisible, setShiftDayModalVisible] = useState(false);

  // TODO:: @abhilashlr: Investigate this if check.
  if (!projectPhaseId) {
    return <></>;
  }

  return (
    <>
      <MoreActionsDropdown
        hideHoverBorderRadius
        menu={renderMenu({
          projectPhase,
          onDeleteProjectPhase: DeleteConfirmation,
          onTogglePrivate,
          setShiftDayModalVisible,
          addPhaseAbove: (): void => {
            addPhaseAbove();
            if (performAutoFocus) {
              performAutoFocus();
            }
          },
        })}
      />
      <ShiftDayModal
        onShiftDays={(shiftBy: number) => {
          onShiftStartDay(shiftBy);
          setShiftDayModalVisible(false);
        }}
        onCancel={() => {
          setShiftDayModalVisible(false);
        }}
        visible={shiftDayModalVisible}
        modalTitle={modalTitle}
        lowerLimit={-projectPhase.startDay + 1}
      />
    </>
  );
};
